import React, { useEffect, useMemo, useState } from 'react';
import { alertService } from '../../services';
import CustomTabs from '../../components/common/tabs/CustomTabs';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addPages, removePage } from '../../redux/reducers/navigationReducer/NavigationSlice';
import {
    changePasswordAction,
    changeSubscriptionAdminAction,
    getAccountAdminsAction,
    getAccountSettingsAction,
    getSubscriptionAdminAction,
    getUserByIdAction,
    updateUserAction,
} from '../../redux/actions/userActions';
import {
    ChangePasswordRequest,
    CreateUserRequest,
    GetAccountSettingsResponse,
    GetSubscriptionAdminResponse,
    UserByIdResponse,
} from '../../redux/actions/userActions/types';
import { useHistory, useParams } from 'react-router-dom';
import DeleteModal from '../../components/common/deleteModal/DeleteModal';
import NotificationsEmail from '../../components/notificationsEmail/NotificationsEmail';
import { clearState } from '../../redux/persistedState';
import { riseStoredUser, setStoredUser } from '../../redux/reducers/authReducer/AuthSlice';
import { extractGetByIdUser } from '../../helpers/extract';
import PageHeader from '../../components/common/pageHeader/PageHeader';
/*
When Strip payment reception online directly from users enabled these cards can be enabled back
import InformationCard from '../../components/common/informationCard/informationCard';
import { getInformationCardButtonText, getInformationCardContent } from './Account.helper';
import {
    DownInformationCard
} from './Account.styles';
*/
import {
    AccountPageSpin,
} from './Account.styles';
import { Margin, PrimaryButton, SpaceEnd, Text } from '../../styles/common.styles';
import { AccountPermissionTypes } from '../../models/permission/enums';
import { SubscriptionTypes } from '../../models/subscription/enums';
import { usePermissions } from '../../hooks/permissions';
import { useModalState } from '../../hooks/modalState';
import { getCustomerPortalLinkAction } from '../../redux/actions/checkoutActions';
import { AlertMessages } from '../../components/alert/AlertMessages';
import { NavigationKey } from '../../components/appNavigationBar/AppNavigation.types';
import { setSubscriptionUpdating } from '../../redux/reducers/subscriptionReducer/SubscriptionSlice';
import ChangeUsersModal from '../../components/ChangeUsersModal/ChangeUsersModal';
import ErrorModal from '../../components/common/errorModal/ErrorModal';
import BasicInformationTab from '../editUser/BasicInformationTab';
import { ActionResponse } from '../../models/actionResponse/types';
import { BasicInfoColumn, BasicInformationTitle, ChangeAdminRow, InlineLink, SubscriptionList, SubscriptionListItem } from '../editUser/BasicInformation.styles';
import { BluedText } from '../users/UsersPage.styles';
import CustomSelect from '../../components/common/select/CustomSelect';
import ConfirmSubscriptionAdminModal from '../../components/common/confirmSubscriptionAdminModal/ConfirmSubscriptionAdminModal';
import { blueDark01 } from '../../styles/colors';

interface Params {
    tab: string;
}

const DOCUMENTATION_LINK = "https://transparencycatalog.zendesk.com/hc/en-us/sections/11967212657298-Account-types-Project-roles";

const Account: React.FC = () => {
    const selector = useAppSelector((state) => state);
    const { authReducer } = selector;
    const dispatch = useAppDispatch();
    const history = useHistory();
    const params = useParams<Params>();
    const { user: storedUser } = authReducer;

    const [user, setUser] = useState<CreateUserRequest | undefined>(undefined);
    const [userSubscriptionType, setUserSubscriptionType] = useState('');
    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
    const { getAccountRole, getAccountSubscription } = usePermissions();
    const { isOpen, onOpen, onClose } = useModalState();
    const [subscriptionCanceled, setSubscriptionCanceled] = useState(false);
    const errorModal = useModalState();
    const [subscriptionAdmin, setSubscriptionAdmin] = useState<any>(null);
    const [selectedNewAdmin, setSelectedNewAdmin] = useState<string>('');
    const [accountAdmins, setAccountAdmins] = useState<Array<{ id: string; name: string; email: string }>>([]);
    const confirmAdminChangeModal = useModalState();
  
    useEffect(() => {
        dispatch(
            addPages([
                {
                    key: NavigationKey.DASHBOARD,
                    value: `${authReducer.user?.firstName}’s Dashboard`,
                },
                {
                    key: NavigationKey.ACCOUNT,
                    value: `Account`,
                },
            ]),
        );
        getData();
        return () => {
            dispatch(removePage());
        };
    }, []);

    const getData = async () => {
        await getUser();
        if (getAccountRole() === AccountPermissionTypes.ACCOUNT_ADMIN) {
            await getAccountSettings();
        }
        await getSubscriptionOwner();
        await getAccountAdmins();
    };

    const getAccountAdmins = async () => {
        const { payload } = (await dispatch(getAccountAdminsAction())) as any;
        if (payload?.data && Array.isArray(payload.data)) {
            const admins = payload.data.map((admin: any) => ({
                id: admin.email,
                name: `${admin.firstName} ${admin.lastName}`,
                email: admin.email,
            }));
            setAccountAdmins(admins);
        }
    };

    const getSubscriptionOwner = async () => {
        try {
            const { payload } = (await dispatch(getSubscriptionAdminAction())) as GetSubscriptionAdminResponse;
            const contact = payload?.data;
            if (contact) {
                const normalized = {
                    firstName: contact.firstName || '',
                    lastName: contact.lastName || '',
                    email: contact.email || null,
                    phoneNumber: contact.phoneNumber || null,
                    phoneNumberExt: contact.phoneNumberExt || null,
                };
                setSubscriptionAdmin(normalized);
            }
        } catch (err) {
            console.warn('Failed to load subscription admin', err);
        }
    };

    const getAccountSettings = async () => {
        const { payload } = (await dispatch(
            getAccountSettingsAction(),
        )) as GetAccountSettingsResponse;
        if (payload.data) {
            setSubscriptionCanceled(payload.data.subscription?.cancelAtPeriodEnd ? true : false);
        }
    };

    const handleChangeSubscriptionAdmin = async () => {
        if (!selectedNewAdmin || user?.email === selectedNewAdmin) {
            return;
        }

        const { payload } = await dispatch(
            changeSubscriptionAdminAction({ newAdminEmail: selectedNewAdmin })
        ) as any;

        if (payload?.message) {
            alertService.success('Subscription Admin changed successfully');
            const { payload } =  await dispatch(getUserByIdAction(authReducer.user?.id.toString() || '')) as ActionResponse<Array<UserByIdResponse>> ;
            dispatch(setStoredUser(payload?.data[0]));
            window.location.reload();
        }
    };

    const getUser = async () => {
        if (authReducer.user?.id) {
            const { payload } = (await dispatch(
                getUserByIdAction(authReducer.user.id.toString()),
            )) as ActionResponse<Array<UserByIdResponse>>;
            if (payload?.data[0]) {
                setUserSubscriptionType(payload.data[0].subscription.planName);
                const currentUser = extractGetByIdUser(payload?.data[0]);
                setUser(currentUser);
            } else {
                history.push('/users');
            }
        }
    };

    const onSubmit = async (data: CreateUserRequest) => {
        Object.keys(data).forEach((key) => {
            if (!data[key]) {
                data[key] = null;
            }
        }, data);
        if (authReducer.user?.id) {
            const { payload } = (await dispatch(
                updateUserAction({
                    user: {
                        ...data,
                        userName: data.email,
                        accountId: user?.parentAccountId || user?.accountId,
                    },
                    id: authReducer.user.id.toString(),
                }),
            )) as any;
            if (payload?.data) {
                if (payload?.data[0].userName !== user?.userName) {
                    clearState();
                    history.push('/login');
                    dispatch(riseStoredUser());
                    alertService.info('Please login with a new email', {
                        autoClose: false,
                    });
                    return;
                } else {
                    dispatch(setStoredUser(payload?.data[0]));
                }
                window.scroll(0, 0);
                alertService.success(AlertMessages.UPDATE_USER);
            } else {
                errorModal.onOpen();
            }
        }
    };

    const submitContainer = (callback: () => void) => {
        return (
            <SpaceEnd>
                <PrimaryButton htmlType="submit" onClick={callback}>
                    Save
                </PrimaryButton>
            </SpaceEnd>
        );
    };

    const submitPassword = async (data: ChangePasswordRequest) => {
        const { payload } = await dispatch(changePasswordAction(data));
        if (payload.message) {
            window.scroll(0, 0);
            alertService.success(AlertMessages.CHANGE_PASSWORD);
        }
    };

    const goToSubscriptions = async () => {
        if (
            getAccountSubscription() === SubscriptionTypes.FREE ||
            getAccountSubscription() === SubscriptionTypes.FREE_TRIAL_INDIVIDUAL ||
            getAccountSubscription() === SubscriptionTypes.FREE_TRIAL_OFFICE
        ) {
            history.push('/plan');
            return;
        }
        if (authReducer.user?.accountId) {
            const { payload } = (await dispatch(getCustomerPortalLinkAction())) as any;
            if (payload) {
                dispatch(setSubscriptionUpdating(true));
                window.location.href = payload.data;
            }
        }
    };

    const upgradeAccount = () => {
        if (getAccountSubscription() === SubscriptionTypes.OFFICE) {
            onOpen();
        } else {
            goToSubscriptions();
        }
    };

    const showLowerInformationCard = () => {
        if (
            getAccountSubscription() === SubscriptionTypes.OFFICE &&
            !authReducer.user?.parentAccountId
        ) {
            return true;
        }
        return false;
    };

    const showSubscriptionCard = () => {
        if(authReducer.user?.accountRole === AccountPermissionTypes.ACCOUNT_ADMIN) {
            return true;
        }
        return false;
    };

    const handleAdminChange = (newAdminEmail: string) => {
        if (newAdminEmail && user?.email !== newAdminEmail) {
            setSelectedNewAdmin(newAdminEmail);
            confirmAdminChangeModal.onOpen();
        }
    };

    const subscriptionAdminCards = useMemo(() => {
        const subAdminIsUser = (storedUserEmail: string | undefined, userEmail: string | undefined, subscriberAdminEmail: string | undefined) => {
            return storedUserEmail === userEmail && userEmail === subscriberAdminEmail;
        }

        if (!subscriptionAdmin) {
            return <BasicInfoColumn style={{ maxWidth: "470px", flex: 2 }}>
                <BasicInformationTitle style={{ width: "100%" }}>
                    Subscription owner
                </BasicInformationTitle>
            </BasicInfoColumn>;
        }
        return <BasicInfoColumn style={{ maxWidth: "470px", flex: 2 }}>
            <BasicInformationTitle style={{ width: "100%" }}>
                Subscription owner
            </BasicInformationTitle>
            <Margin value="0 0 28px 0" />
            {subAdminIsUser(storedUser?.email, user?.email, subscriptionAdmin?.email) ? (
                <Text weight={400} size={13} height={16} color={blueDark01}>You are the <BluedText href={DOCUMENTATION_LINK}>Subscription Admin</BluedText> for Project Builder & Library. Your organization's subscription is connected to your name and email address.
                    <SubscriptionList style={{ marginTop: '8px', marginLeft: '-8px'}}>
                        <SubscriptionListItem>There is only one Subscription Admin.</SubscriptionListItem>
                        <SubscriptionListItem>The Subscription Admin's account cannot be deleted.</SubscriptionListItem>
                        <SubscriptionListItem>
                            You can reassign the Subscription Admin function to another Account Admin.
                        </SubscriptionListItem>
                        <SubscriptionListItem>Only an Account Admin can become the Subscription Admin.</SubscriptionListItem>
                    </SubscriptionList>
                    <ChangeAdminRow>
                        <CustomSelect
                            label={"Change Subscription Admin"}
                            block
                            options={accountAdmins?.filter(admin =>
                                admin.email !== subscriptionAdmin?.email)
                                .map((admin) => ({
                                    id: admin.email,
                                    name: admin.name,
                                }))}
                            value={selectedNewAdmin === '' ? null : selectedNewAdmin}
                            props={{
                                onChange: (value: string) => handleAdminChange(value),
                                placeholder: 'Select new Subscription Admin',
                            }}
                            style={{ width: '354px' }}
                            />
                    </ChangeAdminRow>
                    <Text weight={400} size={13} height={16} color={blueDark01}>
                        Need help? <InlineLink href="mailTo:support@transparencycatalog.com">Contact Sustainable Minds.</InlineLink>
                    </Text>
                </Text>
            ) : (
                <Text weight={400} size={13} height={16} color={blueDark01}>
                    <BluedText href={`mailto:${subscriptionAdmin?.email}`}>
                        {subscriptionAdmin?.firstName} {subscriptionAdmin?.lastName}
                    </BluedText>{' '}
                    is the <BluedText href={DOCUMENTATION_LINK}>Subscription Admin</BluedText>, the business owner for this Project Builder & Library subscription. Contact for questions regarding your account, or for requesting more accounts.
                </Text>
            )}
        </BasicInfoColumn>
    },
    [accountAdmins, selectedNewAdmin, subscriptionAdmin, user?.email, storedUser]);
    
    /*
    When Strip payment reception online directly from users enabled these cards can be enabled back
    const informationCards = (isSubscriptionCanceled: boolean) => () =>
        (
            <div style={{ flex: 1 }}>
                {showSubscriptionCard() && (<InformationCard
                    title={`Subscription type: ${authReducer.user?.subscriptionPlanName}`}
                    content={getInformationCardContent(getAccountSubscription())}
                    buttonText={getInformationCardButtonText(getAccountSubscription())}
                    onClick={upgradeAccount}
                    showButton={getAccountSubscription() !== SubscriptionTypes.OFFICE}
                />)}
                {showLowerInformationCard() && (
                    <DownInformationCard>
                        <InformationCard
                            title="Payment"
                            content={
                                'Visit your Stripe account to update payment information or cancel your subscription.'
                            }
                            buttonText={
                                isSubscriptionCanceled ? 'Renew subscription' : 'Visit Stripe'
                            }
                            onClick={goToSubscriptions}
                            showButton
                        />
                    </DownInformationCard>
                )}
            </div>
        );
    */

    const generateBasicInformation = (isSubscriptionCanceled: boolean) => {
        return (
            <>
                <BasicInformationTab
                    user={user}
                    onSubmit={onSubmit}
                    submitContainer={submitContainer}
                    submitPassword={submitPassword}
                    isOwner={true}
                    //  When Strip payment reception online directly from users enabled these cards can be enabled back
                    //  informationCards={informationCards(isSubscriptionCanceled)}
                    informationCards={() => {
                        return <div style={{ flex: 1 }}></div>
                    }}
                    subscriptionAdminCard={subscriptionAdminCards}
                    userSubscriptionType={userSubscriptionType}
                />
                {isOpen && (
                    <ChangeUsersModal
                        isOpen={isOpen}
                        handleCancel={onClose}
                        currentUsersCount={user?.maxUsersCount}
                        subscriptionName="Office"
                    />
                )}
                {confirmAdminChangeModal.isOpen && (
                    <ConfirmSubscriptionAdminModal
                        isOpen={confirmAdminChangeModal.isOpen}
                        closeModal={() => {
                            confirmAdminChangeModal.onClose();
                            setSelectedNewAdmin('');
                        }}
                        onConfirm={handleChangeSubscriptionAdmin}
                        newAdminName={
                            accountAdmins.find((admin) => admin.email === selectedNewAdmin)?.name || ''
                        }
                    />
                )}
            </>
        );
    };

    const tabs = (isSubscriptionCanceled: boolean) => {
        return [
            {
                name: 'Basic information',
                tab: generateBasicInformation(isSubscriptionCanceled),
                key: '1',
            },
            getAccountSubscription() !== SubscriptionTypes.FREE && ({
                name: 'Notifications & email',
                tab: (
                    <NotificationsEmail
                        id={authReducer.user?.id}
                        userNotifications={user?.notificationSettings || []}
                    />
                ),
                key: '2',
            }),
        ];
    };

    return (
        <>
            <PageHeader
                title={`${authReducer.user?.firstName} ${authReducer.user?.lastName}’s account`}
            />
            <AccountPageSpin size="large" spinning={authReducer.pending}>
                <div>
                    <CustomTabs tabs={tabs(subscriptionCanceled)} defaultKey={params?.tab || '1'} />
                </div>
            </AccountPageSpin>
            <DeleteModal
                isOpen={isOpenDeleteModal}
                closeModal={() => {
                    setIsOpenDeleteModal(false);
                }}
                reload={() => {}}
                text={`${user?.firstName} ${user?.lastName}`}
                title="account"
                icon="trash-can-list.svg"
            />
            {errorModal.isOpen && (
                <ErrorModal isOpen={errorModal.isOpen} closeModal={errorModal.onClose} />
            )}
        </>
    );
};

export default Account;
